import { renderAsEntity } from "./jsx-entity";
import { addComponent } from "bitecs";
import { Networked } from "../bit-components";
import { findAncestorWithComponent } from "./scene-graph";
import { setInitialNetworkedData } from "./assign-network-ids";

/**
 * Shared initializer that mirrors the logic used when a GLTF media-frame component is inflated.
 * It creates a corresponding ECS entity (mediaFrame component), adds the Networked component,
 * assigns deterministic network IDs and parents the new object3D to the provided element.
 *
 * @param {HTMLElement} el           A-Frame entity that owns the media-frame component.
 * @param {Object}      componentData Raw component data passed from GLTF or Scriptable element.
 */
export function mediaFrameEcsInit(el, componentData = {}) {
  if (!window.APP?.world || !el?.object3D) return;

  el.object3D.scriptableType = "media-frame";

  const firstChild = el.object3D.children?.[0];
  if (firstChild && firstChild.name === "Media_Frame") {
    const randomName = Math.random().toString(36).substring(2, 5);
    firstChild.name = `Media_Frame_${randomName}`;
  }

  const entityDef = {
    components: { mediaFrame: componentData },
    attrs: {},
    children: []
  };

  const eid = renderAsEntity(APP.world, entityDef);

  // Network setup -----------------------------------------------------
  if (componentData.networkable?.enabled) {
    addComponent(APP.world, Networked, eid);
  }

  const networkedEl = findAncestorWithComponent(el, "networked");
  const rootNid = (networkedEl && networkedEl.components.networked.data.networkId) || "scene";

  // Prefer GLTF index if available (keeps parity with GLTF behaviour).
  const idx = firstChild?.userData?.gltfIndex !== undefined ? firstChild.userData.gltfIndex : eid;
  setInitialNetworkedData(eid, `${rootNid}.${idx}`, rootNid);

  // Parent ECS-driven object3D under original element for correct transform.
  el.object3D.add(APP.world.eid2obj.get(eid));

  el.setAttribute("open-upload-form", "");

  // Return the eid so callers can use it
  return eid;
}
